import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Loader2,
  AlertCircle,
  ArrowLeft,
  Calendar,
  CheckCircle,
  Lock,
  LockOpen,
  AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import { apiFetch, ApiError } from "@/lib/api-client";
import {
  useFiscalYear,
  type FiscalPeriod,
  type FiscalPeriodStatus,
  type FiscalYearStatus,
} from "@/features/fiscal-calendar";

/**
 * Period status badge configuration.
 */
const periodStatusConfig: Record<
  FiscalPeriodStatus,
  { label: string; className: string; icon: typeof CheckCircle }
> = {
  OPEN: {
    label: "Abierto",
    className: "bg-green-100 text-green-800",
    icon: Calendar,
  },
  LOCKED: {
    label: "Bloqueado",
    className: "bg-yellow-100 text-yellow-800",
    icon: Lock,
  },
  CLOSED: {
    label: "Cerrado",
    className: "bg-gray-100 text-gray-800",
    icon: CheckCircle,
  },
};

/**
 * Year status badge configuration.
 */
const yearStatusConfig: Record<
  FiscalYearStatus,
  { label: string; className: string }
> = {
  OPEN: { label: "Abierto", className: "text-green-600" },
  LOCKED: { label: "Bloqueado", className: "text-yellow-600" },
  CLOSED: { label: "Cerrado", className: "text-gray-600" },
};

/**
 * Format date for display.
 */
function formatDate(dateString: string): string {
  const date = new Date(dateString + "T00:00:00");
  return date.toLocaleDateString("es-CL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

/**
 * Fiscal Year detail page with periods management.
 */
export function CalendarioFiscalDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { year, isLoading, error, refetch } = useFiscalYear(id);

  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [closeYearDialogOpen, setCloseYearDialogOpen] = useState(false);

  // Lock a period
  const handleLockPeriod = async (period: FiscalPeriod) => {
    setActionLoading(period.id);
    try {
      await apiFetch.post(`/api/fiscal-calendar/periods/${period.id}/lock`, {});
      toast.success(`Periodo ${period.name} bloqueado`);
      refetch();
    } catch (err) {
      if (err instanceof ApiError) {
        toast.error(err.message);
      } else {
        toast.error("Error al bloquear el periodo");
      }
    } finally {
      setActionLoading(null);
    }
  };

  // Close a period
  const handleClosePeriod = async (period: FiscalPeriod) => {
    setActionLoading(period.id);
    try {
      await apiFetch.post(`/api/fiscal-calendar/periods/${period.id}/close`, {});
      toast.success(`Periodo ${period.name} cerrado`);
      refetch();
    } catch (err) {
      if (err instanceof ApiError) {
        toast.error(err.message);
      } else {
        toast.error("Error al cerrar el periodo");
      }
    } finally {
      setActionLoading(null);
    }
  };

  // Reopen a period
  const handleReopenPeriod = async (period: FiscalPeriod) => {
    setActionLoading(period.id);
    try {
      await apiFetch.post(`/api/fiscal-calendar/periods/${period.id}/reopen`, {});
      toast.success(`Periodo ${period.name} reabierto`);
      refetch();
    } catch (err) {
      if (err instanceof ApiError) {
        toast.error(err.message);
      } else {
        toast.error("Error al reabrir el periodo");
      }
    } finally {
      setActionLoading(null);
    }
  };

  // Close the fiscal year
  const handleCloseYear = async () => {
    if (!id) return;

    setActionLoading("year");
    try {
      await apiFetch.post(`/api/fiscal-calendar/years/${id}/close`, {});
      toast.success("Ano fiscal cerrado correctamente");
      setCloseYearDialogOpen(false);
      refetch();
    } catch (err) {
      if (err instanceof ApiError) {
        toast.error(err.message);
      } else {
        toast.error("Error al cerrar el ano fiscal");
      }
    } finally {
      setActionLoading(null);
    }
  };

  // Get action button for a period
  const getPeriodAction = (period: FiscalPeriod) => {
    if (year?.status === "CLOSED") {
      return null; // No actions if year is closed
    }

    const isLoading = actionLoading === period.id;

    switch (period.status) {
      case "OPEN":
        return (
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handleLockPeriod(period);
            }}
            disabled={isLoading}
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <Lock className="mr-1 h-3 w-3" />
                Bloquear
              </>
            )}
          </Button>
        );
      case "LOCKED":
        return (
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleClosePeriod(period);
              }}
              disabled={isLoading}
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <CheckCircle className="mr-1 h-3 w-3" />
                  Cerrar
                </>
              )}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleReopenPeriod(period);
              }}
              disabled={isLoading}
            >
              <LockOpen className="h-3 w-3" />
            </Button>
          </div>
        );
      case "CLOSED":
        return (
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handleReopenPeriod(period);
            }}
            disabled={isLoading}
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <LockOpen className="mr-1 h-3 w-3" />
                Reabrir
              </>
            )}
          </Button>
        );
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !year) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-2 text-destructive">
        <AlertCircle className="h-8 w-8" />
        <p>Error al cargar el ano fiscal</p>
        <p className="text-sm text-muted-foreground">
          {error?.message ?? "Ano no encontrado"}
        </p>
        <Button
          variant="outline"
          onClick={() => navigate("/configuracion/calendario-fiscal")}
        >
          Volver al Calendario
        </Button>
      </div>
    );
  }

  const yearStatus = yearStatusConfig[year.status];
  const allPeriodsClosed = year.periods.every((p) => p.status === "CLOSED");
  const canCloseYear = year.status === "OPEN" && allPeriodsClosed;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/configuracion/calendario-fiscal")}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Volver
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-semibold">Ano Fiscal {year.yearNumber}</h1>
          <p className="text-sm text-muted-foreground">
            {formatDate(year.startDate)} - {formatDate(year.endDate)}
            <span className={cn("ml-2 font-medium", yearStatus.className)}>
              ({yearStatus.label})
            </span>
          </p>
        </div>
        {year.status !== "CLOSED" && (
          <Button
            variant="destructive"
            onClick={() => setCloseYearDialogOpen(true)}
            disabled={!canCloseYear}
          >
            <CheckCircle className="mr-2 h-4 w-4" />
            Cerrar Ano
          </Button>
        )}
      </div>

      {/* Year closed notice */}
      {year.status === "CLOSED" && (
        <div className="bg-muted p-4 rounded-lg flex items-center gap-3">
          <CheckCircle className="h-5 w-5 text-green-600" />
          <p className="text-sm">
            Este ano fiscal esta cerrado. No se pueden modificar los periodos.
          </p>
        </div>
      )}

      {/* Close year requirements notice */}
      {year.status !== "CLOSED" && !canCloseYear && (
        <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg flex items-center gap-3">
          <AlertTriangle className="h-5 w-5 text-yellow-600" />
          <p className="text-sm text-yellow-800">
            Para cerrar el ano fiscal, primero debe cerrar todos los periodos.
          </p>
        </div>
      )}

      {/* Periods table */}
      <div className="border rounded-lg overflow-hidden bg-card">
        <div className="bg-muted px-4 py-2">
          <h3 className="font-medium">Periodos</h3>
        </div>
        <table className="w-full">
          <thead>
            <tr className="border-b text-left text-sm text-muted-foreground bg-muted/50">
              <th className="px-4 py-2 font-medium w-12">#</th>
              <th className="px-4 py-2 font-medium">Nombre</th>
              <th className="px-4 py-2 font-medium">Periodo</th>
              <th className="px-4 py-2 font-medium">Estado</th>
              <th className="px-4 py-2 font-medium w-48">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {year.periods.map((period) => {
              const status = periodStatusConfig[period.status];
              const StatusIcon = status.icon;

              return (
                <tr key={period.id} className="border-b last:border-0">
                  <td className="px-4 py-3 text-sm text-muted-foreground">
                    {period.periodNumber}
                  </td>
                  <td className="px-4 py-3 font-medium">{period.name}</td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">
                    {period.startDate === period.endDate
                      ? formatDate(period.startDate)
                      : `${formatDate(period.startDate)} - ${formatDate(period.endDate)}`}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={cn(
                        "inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full",
                        status.className
                      )}
                    >
                      <StatusIcon className="h-3 w-3" />
                      {status.label}
                    </span>
                  </td>
                  <td className="px-4 py-3">{getPeriodAction(period)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Close Year Confirmation Dialog */}
      <AlertDialog
        open={closeYearDialogOpen}
        onOpenChange={setCloseYearDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cerrar Ano Fiscal {year.yearNumber}</AlertDialogTitle>
            <AlertDialogDescription>
              Esta accion es permanente. Una vez cerrado el ano fiscal, no podra
              registrar mas asientos contables ni reabrir periodos.
              <br />
              <br />
              Asegurese de haber completado todos los ajustes de cierre antes de
              continuar.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCloseYear}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={actionLoading === "year"}
            >
              {actionLoading === "year" ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Cerrando...
                </>
              ) : (
                "Cerrar Ano Fiscal"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
